<template>
  <el-dialog
    :visible.sync="visible"
    :width="dialogWidth"
    top="8vh"
    append-to-body
    :close-on-click-modal="false"
    custom-class="wording-dialog-custom"
  >
    <!-- 顶部标题与搜索筛选栏 -->
    <div slot="title" class="dialog-header-wrapper">
      <div class="header-left">
        <span class="header-icon">
          <i class="el-icon-collection"></i>
        </span>
        <span class="header-title">{{ dialogTitle }}</span>
      </div>

      <div class="header-tools">
        <div class="selected-tag" :class="{ 'is-active': selectedIds.length > 0 }">
          <span class="tag-label">Selected</span>
          <span class="tag-count">{{ selectedIds.length }}</span>
        </div>

        <el-select
          v-model="categoryId"
          placeholder="All Categories"
          clearable
          size="small"
          class="compact-category-select"
          @change="fetchData"
        >
          <el-option
            v-for="cat in categoryList"
            :key="cat.id"
            :label="cat.name"
            :value="cat.id"
          />
        </el-select>

        <el-input
          v-model="keywords"
          placeholder="Search wording..."
          prefix-icon="el-icon-search"
          size="small"
          clearable
          class="compact-search-input"
          @input="handleInput"
          @clear="fetchData"
          @keyup.enter.native="fetchData"
        />
      </div>
    </div>

    <!-- 顶部快捷操作及提示条 -->
    <div class="list-sub-toolbar">
      <div class="tips-text">
        <i class="el-icon-info"></i>
        <span>Double-click to insert directly, or check items to batch fill.</span>
      </div>
      <div class="quick-actions" v-if="normalizedList && normalizedList.length > 0">
        <el-button
          type="text"
          size="mini"
          class="batch-action-btn"
          @click="toggleSelectAll"
        >
          {{ isAllSelected ? "Deselect All" : "Select All" }}
        </el-button>
        <span class="action-divider" v-if="selectedIds.length > 0">|</span>
        <el-button
          v-if="selectedIds.length > 0"
          type="text"
          size="mini"
          class="batch-action-btn clear-btn"
          @click="clearSelection"
        >
          Clear
        </el-button>
      </div>
    </div>

    <!-- 列表区域 -->
    <div class="wording-scroll-list" v-loading="loading">
      <template v-if="normalizedList && normalizedList.length > 0">
        <div
          v-for="item in normalizedList"
          :key="item.id"
          class="wording-list-row"
          :class="{ 'is-selected': isSelected(item) }"
          @click="toggleSelect(item)"
          @dblclick.stop="handleDblClick(item)"
        >
          <div class="row-checkbox" @click.stop>
            <el-checkbox
              :value="isSelected(item)"
              @change="() => toggleSelect(item)"
            />
          </div>
          <div class="row-content">
            <div class="item-header-line">
              <span class="item-title">{{ item.title }}</span>
              <span class="dblclick-hint">Double click to insert</span>
            </div>
            <div v-if="item.previewText" class="item-preview">
              {{ item.previewText }}
            </div>
          </div>
        </div>
      </template>

      <!-- 空数据状态 -->
      <div v-else-if="!loading" class="empty-simple">
        <span>No matching wording</span>
      </div>
    </div>

    <!-- 底部操作栏 -->
    <div slot="footer" class="dialog-footer">
      <div class="footer-left-info">
        <span>Total: <strong>{{ normalizedList.length }}</strong> items</span>
        <span class="divider">/</span>
        <span>Selected: <strong class="highlight-count">{{ selectedIds.length }}</strong></span>
      </div>

      <div class="footer-center-lang">
        <el-radio-group v-model="insertLanguage" size="mini" class="lang-radio-group">
          <el-radio-button label="en">En</el-radio-button>
          <el-radio-button label="local">Local</el-radio-button>
        </el-radio-group>
      </div>

      <div class="footer-buttons">
        <el-button size="small" @click="visible = false">Cancel</el-button>
        <el-button
          size="small"
          type="primary"
          icon="el-icon-check"
          :disabled="selectedIds.length === 0"
          @click="handleFillIn"
        >
          Fill-in
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: "WordingModal",
  props: {
    title: {
      type: String,
      default: "Standard Wording",
    },
    width: {
      type: String,
      default: "980px",
    },
    defaultLanguage: {
      type: String,
      default: "en",
    },
    fetchWording: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      visible: false,
      loading: false,
      customTitle: "",
      keywords: "",
      categoryId: "",
      categoryList: [],
      list: [],
      selectedIds: [],
      selectedMap: {},
      insertLanguage: "en",
      searchTimer: null,
    };
  },
  computed: {
    dialogWidth() {
      return this.width || "980px";
    },
    dialogTitle() {
      return this.customTitle || this.title || "Standard Wording";
    },
    normalizedList() {
      return (this.list || []).map((item, index) => {
        if (typeof item === "string") {
          const cleanText = this.stripHtml(item);
          return {
            id: `str_${index}`,
            title: cleanText.length > 40 ? cleanText.slice(0, 40) + "..." : cleanText,
            enContent: item,
            localContent: item,
            content: item,
            previewText: cleanText,
            raw: item,
          };
        }
        const id = item.id != null ? String(item.id) : `idx_${index}`;

        // 解析英文与本地语言内容
        const enRaw = item.descriptionEn || item.contentEn || item.wordingEn || item.textEn || item.en || "";
        const localRaw =
          item.description ||
          item.descriptionLocal ||
          item.descriptionCn ||
          item.descriptionZh ||
          item.contentLocal ||
          item.contentCn ||
          item.local ||
          item.content ||
          item.wording ||
          item.text ||
          item.value ||
          item.body ||
          "";

        const enContent = enRaw || localRaw;
        const localContent = localRaw || enRaw;
        const activeContent = this.insertLanguage === "local" ? localContent : enContent;
        const cleanContent = this.stripHtml(activeContent);

        const title =
          item.title ||
          item.name ||
          item.keyword ||
          item.wordingName ||
          (cleanContent.length > 40 ? cleanContent.slice(0, 40) + "..." : cleanContent) ||
          `Wording ${index + 1}`;
        return {
          id,
          title,
          enContent,
          localContent,
          content: activeContent,
          previewText: cleanContent,
          raw: item,
        };
      });
    },
    selectedItems() {
      return this.selectedIds.map(id => this.selectedMap[id]).filter(Boolean);
    },
    isAllSelected() {
      if (!this.normalizedList || this.normalizedList.length === 0) return false;
      return this.normalizedList.every(item => this.selectedIds.includes(item.id));
    },
  },
  methods: {
    init(options = {}) {
      this.visible = true;
      this.keywords = "";
      this.categoryId = "";
      this.selectedIds = [];
      this.selectedMap = {};
      this.insertLanguage = this.defaultLanguage || "en";
      if (typeof options === "string") {
        this.customTitle = options;
      } else if (options && typeof options === "object") {
        if (options.title) this.customTitle = options.title;
        if (options.language) this.insertLanguage = options.language;
      } else {
        this.customTitle = "";
      }
      this.fetchCategories();
      this.fetchData();
    },
    extractList(res) {
      if (!res) return [];
      if (Array.isArray(res)) return res;
      if (Array.isArray(res.data)) return res.data;
      if (res.data && Array.isArray(res.data.records)) return res.data.records;
      if (res.data && Array.isArray(res.data.list)) return res.data.list;
      if (res.data && Array.isArray(res.data.rows)) return res.data.rows;
      if (Array.isArray(res.records)) return res.records;
      if (Array.isArray(res.list)) return res.list;
      if (Array.isArray(res.rows)) return res.rows;
      if (typeof res === "object" && (res.id != null || res.content || res.wording)) return [res];
      return [];
    },
    async fetchCategories() {
      try {
        const res = await this.$http.get("/qtStandardWordingCategory");
        const list = this.extractList(res);
        this.categoryList = list.map(item => ({
          id: item.id != null ? item.id : item.value || item.categoryId,
          name: item.name || item.categoryName || item.title || item.label || item.description || item.code || String(item.id),
        }));
      } catch (err) {
        console.error("fetch categories error:", err);
        this.categoryList = [];
      }
    },
    isSelected(item) {
      return this.selectedIds.includes(item.id);
    },
    toggleSelect(item) {
      const idx = this.selectedIds.indexOf(item.id);
      if (idx > -1) {
        this.selectedIds.splice(idx, 1);
        this.$delete(this.selectedMap, item.id);
      } else {
        this.selectedIds.push(item.id);
        this.$set(this.selectedMap, item.id, item);
      }
    },
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.normalizedList.forEach(item => {
          const idx = this.selectedIds.indexOf(item.id);
          if (idx > -1) {
            this.selectedIds.splice(idx, 1);
          }
          this.$delete(this.selectedMap, item.id);
        });
      } else {
        this.normalizedList.forEach(item => {
          if (!this.selectedIds.includes(item.id)) {
            this.selectedIds.push(item.id);
          }
          this.$set(this.selectedMap, item.id, item);
        });
      }
    },
    clearSelection() {
      this.selectedIds = [];
      this.selectedMap = {};
    },
    stripHtml(str) {
      if (!str) return "";
      return String(str)
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/\s+/g, " ")
        .trim();
    },
    handleInput() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.fetchData();
      }, 300);
    },
    async fetchData() {
      this.loading = true;
      try {
        let dataList = [];
        if (this.fetchWording) {
          const res = await this.fetchWording(this.keywords, this.categoryId);
          dataList = this.extractList(res);
        } else {
          const params = {};
          if (this.keywords) {
            params.keywords = this.keywords;
          }
          if (this.categoryId != null && this.categoryId !== "") {
            params.categoryId = this.categoryId;
          }
          const res = await this.$http.get("/qtStandardWording", {
            params,
          });
          dataList = this.extractList(res);
        }
        this.list = dataList;
      } catch (err) {
        console.error("fetch wording list error:", err);
        this.list = [];
      } finally {
        this.loading = false;
      }
    },
    handleDblClick(item) {
      if (item) {
        const content =
          typeof item === "string"
            ? item
            : this.insertLanguage === "local"
            ? item.localContent || item.content
            : item.enContent || item.content;
        if (content) {
          this.$emit("insert", content);
          this.visible = false;
        }
      }
    },
    handleFillIn() {
      if (this.selectedItems.length === 0) {
        this.$message.warning("Please select at least one item");
        return;
      }
      const contents = this.selectedItems
        .map(item => {
          if (typeof item === "string") return item;
          return this.insertLanguage === "local" ? item.localContent || item.content : item.enContent || item.content;
        })
        .filter(Boolean);
      if (contents.length === 0) return;

      const hasHtml = contents.some(c => /<[a-z][\s\S]*>/i.test(c));
      const combined = hasHtml ? contents.join("<p><br/></p>") : contents.join("\n\n");
      this.$emit("insert", combined);
      this.visible = false;
    },
  },
  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
  },
};
</script>

<style lang="scss">
.wording-dialog-custom {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);

  .el-dialog__header {
    padding: 16px 20px;
    background: #ffffff;
    border-bottom: 1px solid #ebeef5;
  }

  .el-dialog__body {
    padding: 12px 20px;
    background: #f8fafc;
  }

  .el-dialog__footer {
    padding: 12px 20px;
    background: #ffffff;
    border-top: 1px solid #ebeef5;
  }

  .el-dialog__headerbtn {
    top: 18px;
    right: 20px;

    .el-dialog__close {
      font-size: 16px;
      color: #909399;
      transition: color 0.2s ease;

      &:hover {
        color: #303133;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.dialog-header-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 24px;

  .header-left {
    display: flex;
    align-items: center;
    gap: 8px;

    .header-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 6px;
      background: #ecf5ff;
      color: #409eff;
      font-size: 16px;
    }

    .header-title {
      font-size: 16px;
      font-weight: 600;
      color: #1f2d3d;
      letter-spacing: 0.2px;
    }
  }

  .header-tools {
    display: flex;
    align-items: center;
    gap: 10px;

    .selected-tag {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 3px 10px;
      border-radius: 14px;
      background: #f4f4f5;
      color: #909399;
      font-size: 12px;
      font-weight: 500;
      transition: all 0.2s ease;

      .tag-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 18px;
        height: 18px;
        padding: 0 4px;
        border-radius: 9px;
        background: #e4e7ed;
        color: #606266;
        font-size: 11px;
        font-weight: 600;
      }

      &.is-active {
        background: #ecf5ff;
        color: #409eff;

        .tag-count {
          background: #409eff;
          color: #ffffff;
        }
      }
    }

    .compact-category-select {
      width: 220px;

      ::v-deep .el-input__inner {
        height: 32px;
        line-height: 32px;
        border-radius: 6px;
        border-color: #dcdfe6;
        background: #fff;
        font-size: 13px;

        &:focus {
          border-color: #409eff;
        }
      }
    }

    .compact-search-input {
      width: 200px;

      ::v-deep .el-input__inner {
        height: 32px;
        line-height: 32px;
        border-radius: 6px;
        border-color: #dcdfe6;
        background: #fff;
        font-size: 13px;

        &:focus {
          border-color: #409eff;
        }
      }
    }
  }
}

.list-sub-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 2px 10px;
  font-size: 12px;

  .tips-text {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #909399;

    i {
      font-size: 13px;
      color: #409eff;
    }
  }

  .quick-actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;

    .batch-action-btn {
      padding: 0;
      font-size: 12px;
      color: #409eff;

      &:hover {
        color: #66b1ff;
      }

      &.clear-btn {
        color: #909399;

        &:hover {
          color: #f56c6c;
        }
      }
    }

    .action-divider {
      color: #dcdfe6;
      font-size: 11px;
    }
  }
}

.wording-scroll-list {
  max-height: 440px;
  min-height: 220px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #dcdfe6;
    border-radius: 3px;

    &:hover {
      background-color: #c0c4cc;
    }
  }
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
}

.wording-list-row {
  position: relative;
  display: flex;
  align-items: flex-start;
  padding: 12px 14px;
  margin-bottom: 8px;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;

  &:last-child {
    margin-bottom: 2px;
  }

  &:hover {
    border-color: #d0e2ff;
    background-color: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

    .item-header-line .dblclick-hint {
      opacity: 1;
    }
  }

  &.is-selected {
    border-color: #409eff;
    background-color: #f0f7ff;
    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.08);

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background-color: #409eff;
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
    }
  }

  .row-checkbox {
    margin-right: 12px;
    margin-top: 1px;
    display: flex;
    align-items: center;

    ::v-deep .el-checkbox__inner {
      border-radius: 4px;
    }
  }

  .row-content {
    flex: 1;
    overflow: hidden;

    .item-header-line {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4px;

      .item-title {
        font-size: 14px;
        font-weight: 600;
        color: #303133;
        line-height: 20px;
        word-break: break-word;
      }

      .dblclick-hint {
        font-size: 11px;
        color: #909399;
        opacity: 0;
        transition: opacity 0.2s ease;
        white-space: nowrap;
        margin-left: 8px;
      }
    }

    .item-preview {
      font-size: 13px;
      color: #606266;
      line-height: 20px;
      word-break: break-word;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.empty-simple {
  text-align: center;
  padding: 50px 0;
  color: #909399;
  font-size: 13px;
}

.dialog-footer {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .footer-left-info {
    font-size: 13px;
    color: #606266;
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 200px;

    strong {
      color: #303133;
      font-weight: 600;
    }

    .highlight-count {
      color: #409eff;
      display: inline-block;
      min-width: 14px;
      font-variant-numeric: tabular-nums;
    }

    .divider {
      color: #dcdfe6;
      margin: 0 2px;
    }
  }

  .footer-center-lang {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    display: inline-flex;
    align-items: center;

    .lang-radio-group {
      display: inline-flex;
      align-items: center;
      gap: 8px;

      ::v-deep .el-radio-button {
        &__inner {
          padding: 6px 14px;
          font-size: 12px;
          font-weight: 500;
          border-radius: 4px !important;
          border: 1px solid #dcdfe6 !important;
          box-shadow: none !important;
          transition: all 0.2s ease;
        }

        &.is-active &__inner {
          background-color: #409eff;
          color: #ffffff;
          border-color: #409eff !important;
        }

        &:hover:not(.is-active) &__inner {
          color: #409eff;
          border-color: #c6e2ff !important;
        }
      }
    }
  }

  .footer-buttons {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}
</style>
